import { Portal } from '@linusborg/vue-simple-portal';
import anchor from '../../mixins/anchor';
import transition from '../../mixins/transition';
import {
  setPosition,
  parsePosition,
  validatePosition,
} from '../../utils/position';
import model from '../../mixins/model';

export default {
  name: 'Menu',
  components: {
    Portal,
  },
  mixins: [transition, anchor, model],
  inheritAttrs: false,
  model: {
    prop: 'value',
    event: 'change',
  },
  events: [
    'change',
    'open',
    'close',
    'show',
    'hide',
    'before-show',
    'before-hide',
    'escape-key',
  ],
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    persistent: {
      type: Boolean,
      default: false,
    },
    autoClose: {
      type: Boolean,
      default: true,
    },
    noEscDismiss: {
      type: Boolean,
      default: false,
    },
    anchor: {
      type: String,
      default: 'bottom start',
      validator: validatePosition,
    },
    self: {
      type: String,
      default: 'top start',
      validator: validatePosition,
    },
    fit: {
      type: Boolean,
      default: true,
    },
    offsetHorizontal: {
      type: Number,
      default: 0,
    },
    offsetVertical: {
      type: Number,
      default: 4,
    },
    menuStyle: {
      type: [Object, String, Array],
      required: false,
      default: '',
    },
    scrollTarget: {
      type: [String, Object],
      default: '.scrollarea__thumb',
    },
  },
  data() {
    return {
      scrolling: false,
      scrollEl: null,
      dragging: false,
    };
  },
  watch: {
    value: {
      handler(value) {
        this.showing = value;
      },
      immediate: true,
    },
    showing(value) {
      value ? this.$emit('open') : this.$emit('close');
      this.$emit('change', value);
    },
  },
  beforeMount() {
    window.addEventListener('resize', this.updatePosition);
    window.addEventListener('scroll', this.updatePosition);
    document.addEventListener('mousedown', this.onClickOutside);
    document.addEventListener('mouseup', this.onMouseUp);
    document.addEventListener('touchstart', this.onClickOutside);
    window.addEventListener('keydown', this.onEscapeKey);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updatePosition);
    window.removeEventListener('scroll', this.updatePosition);
    document.removeEventListener('mousedown', this.onClickOutside);
    document.removeEventListener('mouseup', this.onMouseUp);
    document.removeEventListener('touchstart', this.onClickOutside);
    window.removeEventListener('keydown', this.onEscapeKey);
  },
  mounted() {
    /* Update menu pos */
    this.updatePosition();

    this.$watch((vm) => [
      vm.anchorEl,
      vm.target,
      vm.showing,
      vm.self,
      vm.anchor,
      vm.offsetHorizontal,
      vm.offsetVertical,
    ], () => {
      this.updatePosition();
    });

    const anchorResizeObserver = new MutationObserver(this.updatePosition);

    if (this.anchorEl) {
      anchorResizeObserver.observe(
        this.anchorEl,
        {
          attributes: true,
          childList: true,
          subtree: true,
        },
      );
    }
  },
  methods: {
    hide() {
      this.$emit('hide');
    },
    beforeHide() {
      this.$emit('before-hide');
    },
    show() {
      this.$emit('show');
    },
    beforeShow() {
      this.$emit('before-show');
    },
    findScrollElement() {
      let scrollEl;

      if (typeof this.scrollTarget === 'string') {
        scrollEl = document.querySelector(this.scrollTarget);
      } else {
        scrollEl = this.scrollTarget.$el;
      }

      return scrollEl;
    },
    onMouseUp(event) {
      if (!event.target.isSameNode(this.findScrollElement())) this.scrolling = false;
    },
    onClick(event) {
      if (this.autoClose && !this.dragging && !this.scrolling) this.modelHide();

      this.$emit('click', event);
    },
    onEscapeKey(event) {
      if (this.persistent || this.noEscDismiss) return;

      if (event.key === 'Escape') {
        this.modelHide();
      }

      this.$emit('escape-key');
    },
    onClickOutside(event) {
      const target = event.target;
      const menu = this.$refs.menu;
      const anchor = this.anchorEl;

      if (event.target.isSameNode(this.findScrollElement())) this.scrolling = true;
      if (!menu || !anchor) return;

      if (
        this.showing &&
        !this.persistent &&
        !this.scrolling &&
        (
          !menu.contains(target) &&
          !anchor.contains(target)
        )
      ) {
        this.scrolling = false;
        this.modelHide();
      }
    },
    updatePosition() {
      setTimeout(() => {
        if (!this.$refs.menu || !this.anchorEl) return;

        const target = this.$refs.menu;
        const anchor = this.anchorEl;
        const anchorOrigin = parsePosition(this.anchor);
        const selfOrigin = parsePosition(this.self);

        setPosition({
          el: target,
          anchorEl: anchor,
          anchorOrigin,
          selfOrigin,
          offsetHorizontal: this.offsetHorizontal,
          offsetVertical: this.offsetVertical,
          fit: this.fit,
          maxHeight: null,
          maxWidth: null,
        });
      });
    },
  },
};
